import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider
} from "@mui/material";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";
import { handlePagingAll } from "../../components/api/APIUtilities";
import EventSearchTokenTypes from "../../components/utilities/EventSearchTokenTypes";
import { BCPTags, BCPShop } from "../../components/icons/bcp/BCPIcons";

export default function EventTokenTypes() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);

  const fetchUserOrganizations = async () => {
    try {
      setLoading(true);

      const userGameStores = await handlePagingAll(`/gamestores`, {
        myStores: true,
        sortAscending: false,
        "expand[]": ["location", "owner"]
      });

      if (userGameStores && userGameStores.length > 0) {
        setOrganizations(userGameStores);
      } else {
        postNotification("info", "No organizations found for your account.");
      }
    } catch (err) {
      postNotification(
        "error",
        "Failed to load organizations. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authContext?.authState?.identity?.userId) {
      navigate("/login");
      return;
    }

    fetchUserOrganizations();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return <ModalLoadingFull />;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Container
        sx={{
          pt: 2,
          pb: 2
        }}
        maxWidth="lg"
      >
        <Grid container sx={{ gap: 2 }}>
          <Grid
            item
            xs={12}
            sx={{
              p: 2
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: "primary.main",
                  width: 48,
                  height: 48
                }}
              >
                <BCPTags />
              </Avatar>
              <Box>
                <Typography variant="h3">Event Token Types</Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Manage token types for event filtering across your
                  organizations
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          {organizations && organizations.length > 0 ? (
            organizations.map((org) => (
              <Grid key={org.id} item xs={12} sx={{ mb: 1 }}>
                <Card
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: 1,
                    "&:hover": {
                      boxShadow: 2
                    }
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "action.hover"
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 40,
                            height: 40
                          }}
                        >
                          <BCPShop />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary"
                            }}
                          >
                            {org.name}
                          </Typography>
                          {org.location && (
                            <Typography variant="body2" color="text.secondary">
                              {[
                                org.location.city,
                                org.location.state,
                                org.location.country
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <EventSearchTokenTypes
                        organizationId={org.id}
                        showManagement={true}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid
              item
              xs={12}
              sx={{
                p: 2
              }}
            >
              <Card
                sx={{
                  borderRadius: 2,
                  textAlign: "center",
                  p: 4,
                  border: "2px dashed",
                  borderColor: "divider",
                  backgroundColor: "background.paper"
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "action.hover",
                    mb: 2,
                    mx: "auto",
                    width: 56,
                    height: 56
                  }}
                >
                  <BCPShop
                    sx={{ fontSize: "1.5rem", color: "text.secondary" }}
                  />
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  No Organizations Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You need to be associated with an organization to manage token
                  types.
                </Typography>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}
